import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Send, Users, UserPlus, Copy, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareType, setShareType] = useState(null);
  const [shareItems, setShareItems] = useState([]);
  const [selectedShareItem, setSelectedShareItem] = useState(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.other_user.id);
      const interval = setInterval(() => {
        fetchMessages(selectedConversation.other_user.id);
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error("Failed to fetch conversations", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/conversations/${otherUserId}/messages`, { 
        withCredentials: true 
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages", error);
    }
  };

  const searchUsers = async () => {
    if (!userSearchQuery.trim()) return;
    
    try {
      setSearching(true);
      const response = await axios.get(`${API}/users/search`, {
        params: { query: userSearchQuery },
        withCredentials: true
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("User search failed", error);
      toast.error("Failed to search users");
    } finally {
      setSearching(false);
    }
  };

  const startConversation = (user) => {
    setSelectedConversation({
      other_user: user,
      last_message: null,
      last_message_at: null
    });
    setSearchDialogOpen(false);
    setUserSearchQuery("");
    setSearchResults([]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedConversation.other_user.id,
        message: newMessage,
        shared_data: selectedShareItem ? {
          type: shareType,
          data: selectedShareItem
        } : null
      }, { withCredentials: true });

      setNewMessage("");
      setSelectedShareItem(null);
      setShareType(null);
      fetchMessages(selectedConversation.other_user.id);
      fetchConversations();
    } catch (error) {
      console.error("Failed to send message", error);
      toast.error("Failed to send message");
    }
  };

  const openShareDialog = async (type) => {
    setShareType(type);
    try {
      const endpoint = type === "prospect" ? "/prospects" : "/visits";
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setShareItems(response.data);
      setShareDialogOpen(true);
    } catch (error) {
      console.error("Failed to load items", error);
      toast.error("Failed to load items to share");
    }
  };

  const selectShareItem = (item) => {
    setSelectedShareItem(item);
    setShareDialogOpen(false);
    toast.success(`Selected ${shareType} to share`);
  };

  const copySharedData = async (messageId) => {
    try {
      const response = await axios.post(`${API}/messages/shared-data/${messageId}/copy`, {}, {
        withCredentials: true
      });
      toast.success(`${response.data.type === "prospect" ? "Prospect" : "Contact"} copied to your database!`);
    } catch (error) {
      console.error("Failed to copy shared data", error);
      toast.error("Failed to copy shared data");
    }
  };

  return (
    <PageLayout title="Messages">
      <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Conversations List */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-conversation-button" className="w-full bg-emerald-700 hover:bg-emerald-800">
                  <UserPlus className="w-4 h-4 mr-2" />
                  New Conversation
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Find User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      data-testid="user-search-input"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                    />
                    <Button onClick={searchUsers} disabled={searching}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  {searchResults.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {searchResults.map(user => (
                        <div
                          key={user.id}
                          onClick={() => startConversation(user)}
                          className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                        >
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No conversations yet</p>
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedConversation?.other_user?.id === conv.other_user?.id ? 'bg-emerald-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={conv.other_user?.picture} 
                      alt={conv.other_user?.name} 
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{conv.other_user?.name}</p>
                      <p className="text-sm text-gray-500 truncate">{conv.last_message || "Start a conversation"}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col">
          {selectedConversation ? (
            <>
              <div className="p-4 border-b border-gray-200 flex items-center space-x-3">
                <img 
                  src={selectedConversation.other_user.picture} 
                  alt={selectedConversation.other_user.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-900">{selectedConversation.other_user.name}</p>
                  <p className="text-xs text-gray-500">{selectedConversation.other_user.email}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <MessageBubble 
                    key={msg.id} 
                    message={msg}
                    onCopy={() => copySharedData(msg.id)}
                  />
                ))}
              </div>

              <div className="p-4 border-t border-gray-200">
                {selectedShareItem && (
                  <div className="mb-2 p-2 bg-emerald-50 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-emerald-800">
                      Sharing: {selectedShareItem.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedShareItem(null);
                        setShareType(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                
                <form onSubmit={sendMessage} className="flex gap-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openShareDialog("prospect")}
                      title="Share Prospect"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openShareDialog("visit")}
                      title="Share Contact"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    data-testid="message-input"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                  />
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select {shareType === "prospect" ? "Prospect" : "Contact"} to Share</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {shareItems.map(item => (
              <div
                key={item.id}
                onClick={() => selectShareItem(item)}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <p className="font-medium">{item.name}</p>
                {item.contact && <p className="text-sm text-gray-500">{item.contact}</p>}
                {item.address && <p className="text-sm text-gray-500">{item.address}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

function MessageBubble({ message, onCopy }) {
  const isReceived = message.sender_id !== message.receiver_id;
  
  return (
    <div className={`flex ${isReceived ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-xs lg:max-w-md ${
        isReceived ? 'bg-gray-100' : 'bg-emerald-600 text-white'
      } rounded-lg p-3`}>
        <p className="text-sm">{message.message}</p>
        
        {message.shared_data && (
          <div className={`mt-2 p-2 rounded ${
            isReceived ? 'bg-white' : 'bg-emerald-700'
          }`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className={`text-xs font-semibold ${isReceived ? 'text-gray-700' : 'text-white'}`}>
                  Shared {message.shared_data.type === "prospect" ? "Prospect" : "Contact"}
                </p>
                <p className={`text-xs ${isReceived ? 'text-gray-600' : 'text-emerald-100'}`}>
                  {message.shared_data.data?.name}
                </p>
              </div>
              {isReceived && (
                <Button
                  onClick={onCopy}
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  title="Copy to my database"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        )}
        
        <p className={`text-xs mt-1 ${isReceived ? 'text-gray-500' : 'text-emerald-100'}`}>
          {format(new Date(message.created_at), 'HH:mm')}
        </p>
      </div>
    </div>
  );
}
